import React, { Component } from 'react'
import { connect } from 'react-redux'
import isEmpty from 'lodash/isEmpty'

// Components
import Tooltip from './../utils/Tooltip'

// CSS
import './../../css/button.css'

// State
import { addToCart, clearSuccess } from './../../store/actions/cart'

// Utils (getInventory is safe for SSR)
import getInventory from '../../utils/inventoryUtils'

// Helper: collect participating option ids (those that appear in any variant_sku.ids)
function getParticipatingOptionIds(product) {
	const set = new Set()
	if (product && product.variant_skus) {
		product.variant_skus.forEach(vs => {
			if (vs.ids) vs.ids.forEach(id => set.add(id))
		})
	}
	return set
}

// Helper: build filtered variantsSelected containing only participating options
function filterParticipatingSelections(product, variantsSelected) {
	if (!variantsSelected || !variantsSelected.length) return []
	const participating = getParticipatingOptionIds(product)
	return variantsSelected.filter(v => participating.has(v.option_id))
}

// Helper: build human readable variant string from filtered selections
function buildVariantString(product, filteredSelections) {
	if (!product || !product.variants || !filteredSelections.length) return ''
	const parts = []
	filteredSelections.forEach(sel => {
		const group = product.variants.find(v => `${v.title}-${v.id}` === sel.variant_id)
		if (group) {
			const opt = group.items.find(i => i.id === sel.option_id)
			if (opt) parts.push(opt.title)
		}
	})
	return parts.join(' ')
}

class ProductBuyBtn extends Component {
	constructor(props) {
		super(props)
		this.componentCleanup = this.componentCleanup.bind(this)
		this.state = {
			quantityError: false,
		}
	}

	componentDidMount() {
		window.addEventListener('beforeunload', this.componentCleanup)
	}

	componentWillUnmount() {
		this.componentCleanup()
	}

	componentCleanup() {
		this.props.clearSuccess()
		window.removeEventListener('beforeunload', this.componentCleanup)
	}

	addToCart() {
		const { product, variantsSelected } = this.props
		// Filter to only SKU-participating selections
		const filtered = filterParticipatingSelections(product, variantsSelected)
		// Determine required group count (only participating groups)
		let requiredGroups = 0
		if (product && product.variants) {
			const participatingIds = getParticipatingOptionIds(product)
			product.variants.forEach(v => {
				if (v.items && v.items.some(it => participatingIds.has(it.id))) requiredGroups += 1
			})
		}
		if (requiredGroups > 0 && filtered.length < requiredGroups) {
			this.setState({ quantityError: true })
			return
		}
		if (this.checkInventoryQuantity()) {
			// Send filtered selections only
			this.props.addToCart(product.id, this.props.quantity, filtered)
			let categories = ''
			for (let i = 0; i < this.props.structure.length; i++) {
				if (categories !== '') categories += '/'
				categories += this.props.structure[i].title
			}
			const variant_title = buildVariantString(product, filtered)
			const productData = { id: product.sku, name: product.title, category: categories, variant: variant_title }
			console.log('Calling GA addToCart()', productData)
			if (typeof window !== 'undefined') {
				const { addToCart: addToCartGA } = require('../../utils/google-analytics')
				addToCartGA(productData, this.props.final_price, this.props.quantity, variant_title)
			}
		}
	}

	checkInventoryQuantity() {
		const inventory = getInventory(this.props.product, this.props.variant)
		const quantityEntered = parseInt(this.props.quantity, 10)
		const quantityInCartObj = this.props.items.find(item =>
			item.variant
				? item.variant.sku_id === (this.props.variant ? this.props.variant.id : this.props.variant)
				: item.product_id === this.props.product.id
		)
		const quantityCart = quantityInCartObj === undefined ? 0 : parseInt(quantityInCartObj.qty, 10)

		if (inventory === null || inventory === undefined) return this.hideQuantityTooltipError()

		if (quantityEntered <= inventory) {
			if (quantityInCartObj === undefined) return this.hideQuantityTooltipError()

			if (quantityEntered + quantityCart <= inventory) return this.hideQuantityTooltipError()
			else return this.showQuantityTooltipError()
		} else {
			return this.showQuantityTooltipError()
		}
	}

	showQuantityTooltipError() {
		this.props.clearSuccess()
		this.setState({ quantityError: true })
		return false
	}

	hideQuantityTooltipError() {
		this.setState({ quantityError: false })
		return true
	}

	render() {
		const { product, variantsSelected } = this.props
		let inv = getInventory(product, this.props.variant)
		let disabled = inv > 0 || inv === undefined || inv === null ? false : true
		let btnText = inv > 0 || inv === undefined || inv === null ? 'Add to bag' : 'Out of stock'
		// Compute selection readiness
		const participatingIds = getParticipatingOptionIds(product)
		let requiredGroups = 0
		if (product && product.variants) {
			product.variants.forEach(v => {
				if (v.items && v.items.some(it => participatingIds.has(it.id))) requiredGroups += 1
			})
		}
		const filtered = filterParticipatingSelections(product, variantsSelected)
		if (requiredGroups > 0 && filtered.length < requiredGroups) {
			disabled = true
			btnText = 'Select options'
		}
		if (this.props.inProcess) disabled = true

		let showErrorTooltip = this.props.error !== undefined
		let showSuccessTooltip = this.props.success !== undefined
		let showQuantityError = {
			qty: `There is only ${getInventory(
				this.props.product,
				this.props.variant
			)} of this item currently in stock.`,
		}

		return (
			<div className='btn_container'>
				{this.state.quantityError && (
					<Tooltip parent={this.addBtn} type={'dialog--error'} messageObjects={showQuantityError} />
				)}
				<button
					className='btn buy_btn'
					ref={addBtn => (this.addBtn = addBtn)}
					disabled={disabled}
					onClick={() => this.addToCart()}
				>
					{btnText}
				</button>
				{showErrorTooltip && this.addBtn && (
					<Tooltip parent={this.addBtn} type={'dialog--error'} messageObjects={this.props.error} />
				)}
				{showSuccessTooltip && this.addBtn && (
					<Tooltip
						parent={this.addBtn}
						type={'dialog--success'}
						messageObjects={{ success: this.props.success }}
					/>
				)}
			</div>
		)
	}
}

function mapStateToProps(state) {
	return {
		items: state.cart.items,
		inProcess: state.cart.inProcess,
		error: state.cart.error,
		success: state.cart.success,
		variantsSelected: state.productVariant.variant_options,
		variant: state.productVariant.variant,
		final_price: state.product.final_price,
	}
}

export default connect(mapStateToProps, { addToCart, clearSuccess })(ProductBuyBtn)



// WEBPACK FOOTER //
// ./src/components/product/ProductBuyBtn.js